import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon } from '../atoms/Icon';
import { usePresence } from '../hooks/usePresence';
import styles from './FacetButton.module.css';

export interface FacetButtonProps {
  /** Name of the facet, e.g. "Sorte". */
  label: string;
  /** Current value shown after the name, e.g. "Wald". */
  valueLabel?: string;
  /** Highlighted with the clear button; false for the neutral state. */
  active: boolean;
  /** e.g. "Sorte: Wald, Filter entfernen". */
  clearLabel: string;
  onClear: () => void;
  /** The menu content; call `close` after a single choice. */
  children: (close: () => void) => ReactNode;
}

/**
 * One filter facet in the toolbar of a list (docs/ux-richtlinien.md §6.3): a
 * button showing name and value that opens its menu directly. The clear
 * button removes the filter without opening the menu.
 */
export function FacetButton({
  label,
  valueLabel,
  active,
  clearLabel,
  onClear,
  children,
}: FacetButtonProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { present, state } = usePresence(open, panelRef);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    }
  };

  return (
    // Escape closes the menu from anywhere inside; the keys act on the buttons within.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div ref={rootRef} className={styles.root} onKeyDown={onKeyDown}>
      <div className={[styles.facet, active && styles.active].filter(Boolean).join(' ')}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((o) => !o)}
        >
          {valueLabel ? `${label}: ${valueLabel}` : label}
          {!active && <Icon name="chevron-down" size="xs" />}
        </button>
        {active && (
          <button
            type="button"
            className={styles.clear}
            aria-label={clearLabel}
            onClick={() => {
              setOpen(false);
              onClear();
            }}
          >
            <Icon name="close" size="xs" />
          </button>
        )}
      </div>
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-label={label}
        hidden={!present}
        data-state={state}
        className={styles.panel}
      >
        {present && children(close)}
      </div>
    </div>
  );
}

export interface FacetOption {
  value: string;
  label: string;
}

export interface FacetListProps {
  label: string;
  options: FacetOption[];
  value: string;
  onSelect: (value: string) => void;
  /** Placeholder of the search field, shown from `searchFrom` options on. */
  searchLabel: string;
  searchFrom?: number;
}

/** Single choice with check marks; search field for long lists, arrow keys. */
export function FacetList({
  label,
  options,
  value,
  onSelect,
  searchLabel,
  searchFrom = 8,
}: FacetListProps) {
  const [needle, setNeedle] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const searchable = options.length >= searchFrom;
  const shown = options.filter((o) =>
    o.label.toLocaleLowerCase('de').includes(needle.trim().toLocaleLowerCase('de')),
  );
  useEffect(() => {
    const target = searchable
      ? listRef.current?.parentElement?.querySelector<HTMLElement>('input')
      : (listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]') ??
        listRef.current?.querySelector<HTMLElement>('[role="option"]'));
    target?.focus();
  }, [searchable]);
  const move = (e: ReactKeyboardEvent, delta: number) => {
    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [],
    );
    if (items.length === 0) return;
    e.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = index === -1 ? (delta > 0 ? 0 : items.length - 1) : index + delta;
    items[(next + items.length) % items.length]?.focus();
  };
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') move(e, 1);
    else if (e.key === 'ArrowUp') move(e, -1);
    else if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && shown[0]) {
      e.preventDefault();
      onSelect(shown[0].value);
    }
  };
  return (
    // Arrow keys move between the options; the keys act on the controls within.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={onKeyDown}>
      {searchable && (
        <input
          type="search"
          className={styles.search}
          value={needle}
          placeholder={searchLabel}
          aria-label={searchLabel}
          onChange={(e) => setNeedle(e.target.value)}
        />
      )}
      <div ref={listRef} role="listbox" aria-label={label} className={styles.list}>
        {shown.map((o) => (
          <button
            key={o.value}
            type="button"
            role="option"
            aria-selected={o.value === value}
            className={styles.option}
            onClick={() => onSelect(o.value)}
          >
            <span className={styles.check} aria-hidden="true">
              {o.value === value && <Icon name="check" size="sm" />}
            </span>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
